import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, LogoutOutlined } from "@ant-design/icons";
import Cookies from "js-cookie";
import { Country, State, City } from "country-state-city";
import axios from "axios";
import { FaSpinner } from "react-icons/fa6";

const { Option } = Select;

const UserForm = () => {
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [loading, setLoading] = useState();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCountries = () => {
      const countryData = Country.getAllCountries();
      setCountries(countryData);
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const countryObj = countries.find(
        (item) => item.name === selectedCountry
      );
      const isoCode = countryObj.isoCode;
      const stateData = State.getStatesOfCountry(isoCode);
      setStates(stateData);
      setCities([]); // Reset cities when country changes
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState) {
      const stateObj = states.find((item) => item.name === selectedState);
      const stateIsoCode = stateObj.isoCode;
      const countryCode = stateObj.countryCode;
      const cityData = City.getCitiesOfState(countryCode, stateIsoCode);
      setCities(cityData);
    } else {
      setCities([]);
    }
  }, [selectedState, selectedCountry]);

  const signOut = () => {
    Cookies.remove("jwtToken");
    navigate("/login");
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    form.setFieldsValue({ state: "", city: "" });
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    form.setFieldsValue({ city: "" });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/individual/onboarding`,
        values
      );
      Cookies.set("jwtToken", response.data.access_token);
      navigate("/onboarding/userdocuments");
    } catch (error) {
      console.error("Error submitting form:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left Side: Background Image */}
      <div
        className="w-3/5 h-full bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://d1irs7zslorh1g.cloudfront.net/assets/slider/hands-cupping-heart-love-modified.jpg")',
        }}
      ></div>

      {/* Right Side: Form */}
      <div className="w-2/5 h-full bg-white  border rounded-[20px] relative overflow-y-auto">
      <div
          className="sticky top-0 z-10 flex justify-between items-center bg-white p-10 w-full shadow-md "
        >
          <Button
            className="flex items-center text-gray-600"
            onClick={() => navigate(-1)}
          >
            <ArrowLeftOutlined />
            Back
          </Button>
          <Button className="flex items-center text-gray-600" onClick={signOut}>
            <LogoutOutlined />
            Logout
          </Button>
        </div>

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          initialValues={{ country: "", state: "", city: "" }}
          className="space-y-4 mt-8 p-4"
        >
          <h2
            className="text-3xl text-center mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Individual Form
          </h2>

          <div className="flex flex-wrap -mx-2">
            <div className="w-full md:w-1/2 p-2">
              <Form.Item
                label="Pan Card"
                name="pancard"
                rules={[
                  {
                    required: true,
                    message: "Please input your PAN card number!",
                  },
                  {
                    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                    message: "Invalid PAN card format!",
                  },
                ]}
              >
                <Input className="border rounded-xl p-2 w-full uppercase" />
              </Form.Item>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <Form.Item
                label="Address Line 1"
                name="address_line_1"
                rules={[
                  {
                    required: true,
                    message: "Please input your address line 1!",
                  },
                ]}
              >
                <Input className="border rounded-xl p-2 w-full" />
              </Form.Item>
            </div>
          </div>

          <div className="p-2">
            <Form.Item
              label="Address Line 2"
              name="address_line_2"
              rules={[
                {
                  required: true,
                  message: "Please input your address line 2!",
                },
              ]}
            >
              <Input className="border rounded-xl p-2 w-full" />
            </Form.Item>
          </div>
          <div className="flex justify-between gap-4  ">
          <div className="p-2">
            <Form.Item
              label="Country"
              name="country"
              rules={[
                { required: true, message: "Please select your country!" },
              ]}
            >
              <Select
                onChange={handleCountryChange}
                className="w-full border rounded-xl"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
                placeholder="Select Country"
              >
                <Option value="">Select Country</Option>
                {countries.map((country) => (
                  <Option key={country.isoCode} value={country.name}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        

          <div className="p-2">
            <Form.Item
              label="State"
              name="state"
              rules={[{ required: true, message: "Please select your state!" }]}
            >
              <Select
                onChange={handleStateChange}
                className="w-full border rounded-xl"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                <Option value="">Select State</Option>
                {states.map((state) => (
                  <Option key={state.isoCode} value={state.name}>
                    {state.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="p-2">
            <Form.Item
              label="City"
              name="city"
              rules={[{ required: true, message: "Please select your city!" }]}
            >
              <Select
                className="w-full border rounded-xl"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                  
                <Option value="">Select City</Option>
                {cities.map((city) => (
                  <Option key={city.name} value={city.isoCode}>
                    {city.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            </div>
          </div>

          <div className="p-2">
            <Form.Item
              label="Pincode"
              name="postcode"
              rules={[
                { required: true, message: "Please input your postcode!" },
              ]}
            >
              <Input className="border rounded-xl p-2 w-full" />
            </Form.Item>
          </div>

          <div className="flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              className={`bg-blue-500 hover:bg-blue-700 text-white py-2 min-w-[120px] h-10 rounded-xl flex justify-center items-center transition duration-300 ease-in-out ${
                loading ? "cursor-not-allowed opacity-75" : ""
              }`}
              disabled={loading} // Disable the button while loading
            >
              {loading ? (
                <FaSpinner className="animate-spin mr-2 text-white" /> // Spinner with Tailwind animation
              ) : (
                "Submit Form"
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default UserForm;
